// deps
import { useCallback, useEffect, useRef, useState } from "react";

// internal-deps
import { getCloudinaryRawUrl, TEAM_LINE } from "@swiggy-private/js-utils";
import { CHECKOUT_WEB_WP, useCheckoutWebDynamicConfig, useSessionStorage } from "@swiggy-private/checkout-core";
import { captureError, isEmpty } from "@swiggy-private/core-ui";

// helpers
import {
    canShowSavingsStamp,
    shouldFetchSavingsStampLottie,
    updateSavingsStampLottieData,
    updateSavingsStampSidCount,
} from "@cart-ui/helpers/SavingsStamp";

// constants
import { IS_SAVINGS_STAMP_SHOWN, SAVINGS_STAMP_LOTTIE_ID } from "../constants";

interface UseSavingsStamp {
    xpEnabled: boolean;
    title: string;
    subtitle: string;
    isBillCollapsible: boolean;
    isBillUncollapsed: boolean;
    onImpressionSavingsStamp: (context: Record<string, unknown>) => void;
}

export function useSavingsStamp({
    xpEnabled,
    title,
    subtitle,
    isBillCollapsible,
    isBillUncollapsed,
    onImpressionSavingsStamp,
}: UseSavingsStamp) {
    const { checkoutWebWP } = useCheckoutWebDynamicConfig();
    const [hasLottiePlayed, setHasLottiePlayed] = useSessionStorage(IS_SAVINGS_STAMP_SHOWN, false);

    const [lottieData, setLottieData] = useState<object | null>(null);
    const [lottieFailed, setLottieFailed] = useState(false);

    const prevTitle = useRef<string>();
    const prevSubtitle = useRef<string>();

    const onBillEnter = useCallback(() => {
        if (
            !canShowSavingsStamp({
                xpEnabled,
                title,
                subtitle,
                hasLottiePlayed,
                checkoutWebWPSidLimit: checkoutWebWP[CHECKOUT_WEB_WP.WEB_CART_SAVINGS_STAMP_FREQUENCY_THRESHOLD],
                lottieData,
                isBillCollapsible,
                isBillUncollapsed,
            })
        ) {
            return;
        }

        updateSavingsStampSidCount();
        onImpressionSavingsStamp({ savingsAmount: subtitle });
        setHasLottiePlayed(true);
    }, [
        xpEnabled,
        title,
        subtitle,
        hasLottiePlayed,
        checkoutWebWP,
        lottieData,
        isBillCollapsible,
        isBillUncollapsed,
        onImpressionSavingsStamp,
        setHasLottiePlayed,
    ]);

    // fetch lottie data
    useEffect(() => {
        const fetchSavingsStampLottie = async () => {
            const lottieUrl = getCloudinaryRawUrl(SAVINGS_STAMP_LOTTIE_ID, {}, { teamLine: TEAM_LINE.PORTAL }) ?? "";

            try {
                const response = await fetch(lottieUrl);
                const data = await response.json();

                const updatedData = updateSavingsStampLottieData(data, title, subtitle);

                setLottieData(updatedData);
            } catch (error) {
                captureError(error);
                setLottieFailed(true);
            }
        };

        if (shouldFetchSavingsStampLottie({ xpEnabled, title, subtitle, lottieData, lottieFailed })) {
            fetchSavingsStampLottie();
        }
    }, [lottieData, lottieFailed, title, subtitle, xpEnabled]);

    // compare previous title/subtitle with current title/subtitle
    useEffect(() => {
        if (isEmpty(lottieData)) {
            return;
        }
        if (prevTitle.current !== title || prevSubtitle.current !== subtitle) {
            const updatedLottieData = updateSavingsStampLottieData(lottieData, title, subtitle);
            setLottieData(updatedLottieData);
            setHasLottiePlayed(false);
        }

        prevTitle.current = title;
        prevSubtitle.current = subtitle;
    }, [title, subtitle, lottieData, setHasLottiePlayed]);

    return { showLottie: hasLottiePlayed, lottieData, onBillEnter };
}
